import React, { useState } from "react";
import {
  Mail,
  Globe,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import config from "../config";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const consultancy = config.services.find((s) => s.id === "consultancy");
  const consultationRate = consultancy ? consultancy.price : "$30/hour";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!formData.name.trim()) return "Name is required.";
    if (!formData.email.trim()) return "Email is required.";
    if (!/\S+@\S+\.\S+/.test(formData.email))
      return "Please enter a valid email address.";
    if (!formData.subject.trim()) return "Subject is required.";
    if (!formData.message.trim()) return "Message is required.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message || "Failed to send message. Please try again."
        );
      }

      setSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err: any) {
      setError(
        err.message || "Something went wrong. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClasses =
    "w-full rounded-lg border border-white/10 bg-slate-800/60 px-4 py-3 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50";

  return (
    <div className="pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/5 bg-slate-950 py-20">
        <div className="animate-gradient-bg absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 opacity-60" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-slate-400">
            Have a project in mind? We&rsquo;d love to hear from you. Send us a
            message and we&rsquo;ll respond as soon as possible.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-3">
          {/* Form */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-white">
              Send Us a Message
            </h2>

            {success && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
                <CheckCircle size={18} />
                Your message has been sent successfully! We&rsquo;ll get back to
                you soon.
              </div>
            )}

            {error && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1.5 block text-sm font-medium text-slate-300"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    disabled={loading}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-slate-300"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    disabled={loading}
                    className={inputClasses}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="subject"
                  className="mb-1.5 block text-sm font-medium text-slate-300"
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this about?"
                  disabled={loading}
                  className={inputClasses}
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="mb-1.5 block text-sm font-medium text-slate-300"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project…"
                  rows={6}
                  disabled={loading}
                  className={inputClasses}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-8 py-3.5 font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
              >
                {loading ? (
                  "Sending…"
                ) : (
                  <>
                    Send Message <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Get in Touch</h2>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <Mail className="mt-0.5 shrink-0 text-cyan-400" size={22} />
                <div>
                  <h3 className="font-semibold text-white">Email</h3>
                  <a
                    href={`mailto:${config.company.email}`}
                    className="text-sm text-slate-400 hover:text-cyan-400"
                  >
                    {config.company.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <Globe className="mt-0.5 shrink-0 text-cyan-400" size={22} />
                <div>
                  <h3 className="font-semibold text-white">Website</h3>
                  <a
                    href={config.company.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-400 hover:text-cyan-400"
                  >
                    {config.company.domain}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <Clock className="mt-0.5 shrink-0 text-cyan-400" size={22} />
                <div>
                  <h3 className="font-semibold text-white">Consultation</h3>
                  <p className="text-sm text-slate-400">{consultationRate}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
